import { useEffect } from 'react';
import { useSimulationStore } from '../../stores/simulationStore';
import type { FaultConfig, FaultSeverity } from '../../types';

const FAULT_CATEGORIES: { label: string; targets: string[] }[] = [
  { label: 'Navigation', targets: ['gps_position', 'compass_heading'] },
  { label: 'Depth', targets: ['depth_sounder'] },
  { label: 'Wind', targets: ['wind_sensor'] },
  { label: 'Engine', targets: ['engine_coolant', 'engine_oil_pressure', 'engine_exhaust'] },
  { label: 'Electrical', targets: ['house_battery', 'alternator', 'solar_panel'] },
];

const SEVERITY_OPTIONS: { value: FaultSeverity; label: string; color: string }[] = [
  { value: 'mild', label: 'Mild', color: 'bg-yellow-600' },
  { value: 'moderate', label: 'Mod', color: 'bg-orange-600' },
  { value: 'severe', label: 'Severe', color: 'bg-red-600' },
];

function FaultItem({ fault }: { fault: FaultConfig }) {
  const { enableFault, disableFault, setFaultSeverity } = useSimulationStore();

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        fault.enabled
          ? 'border-red-500/50 bg-red-950/30'
          : 'border-nautical-border bg-nautical-dark'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-200">{fault.name}</span>
        <button
          className={`w-10 h-5 rounded-full transition-colors relative ${
            fault.enabled ? 'bg-red-600' : 'bg-nautical-darker'
          }`}
          onClick={() => (fault.enabled ? disableFault(fault.id) : enableFault(fault.id))}
        >
          <div
            className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${
              fault.enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-2">{fault.description}</p>
      <div className="flex space-x-1">
        {SEVERITY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`px-2 py-0.5 rounded text-xs transition-colors ${
              fault.severity === opt.value
                ? `${opt.color} text-white`
                : 'bg-nautical-darker text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => setFaultSeverity(fault.id, opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FaultPanel() {
  const { faultCatalog, fetchFaultCatalog, clearAllFaults, activeFaultCount } =
    useSimulationStore();

  useEffect(() => {
    fetchFaultCatalog();
  }, [fetchFaultCatalog]);

  return (
    <div className="space-y-4">
      {activeFaultCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-400">
            {activeFaultCount} fault{activeFaultCount !== 1 ? 's' : ''} active
          </span>
          <button
            className="text-xs text-red-400 hover:text-red-300 underline"
            onClick={clearAllFaults}
          >
            Clear All
          </button>
        </div>
      )}

      {FAULT_CATEGORIES.map((cat) => {
        const faults = faultCatalog.filter((f) => cat.targets.includes(f.target));
        if (faults.length === 0) return null;

        return (
          <div key={cat.label}>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              {cat.label}
            </h4>
            <div className="space-y-2">
              {faults.map((fault) => (
                <FaultItem key={fault.id} fault={fault} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
